import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to get auth token from sessionStorage
const getAuthToken = () => {
  const adminData = sessionStorage.getItem('adminkhadamat');
  if (adminData) {
    try {
      const parsed = JSON.parse(adminData);
      // البحث عن التوكن في أماكن مختلفة
      if (parsed.data?.token?.access_token) {
        return String(parsed.data.token.access_token);
      }
      if (parsed.data?.access_token) {
        return String(parsed.data.access_token);
      }
      if (parsed.data?.token) {
        return String(parsed.data.token);
      }
      if (parsed.token) {
        return String(parsed.token);
      }
      return null;
    } catch {
      return null;
    }
  }
  return null;
};

const initialState = {
  promoCodes: [],
  promoCode: null,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  },
  loading: false,
  error: null,
  success: null
};

// Get all promo codes with filters
export const fetchPromoCodes = createAsyncThunk(
  'adminPromoCodes/fetchPromoCodes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        from = '',
        to = '',
        code = '',
        status = '',
        created_by_type = ''
      } = params;

      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page);
      if (from) queryParams.append('from', from);
      if (to) queryParams.append('to', to);
      if (code) queryParams.append('code', code);
      if (status) queryParams.append('status', status);
      if (created_by_type) queryParams.append('created_by_type', created_by_type);

      const response = await axios.get(`${BASE_URL}/admin/promo-codes?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب بيانات البرومو كود');
    }
  }
);

// Get single promo code details
export const fetchPromoCodeDetails = createAsyncThunk(
  'adminPromoCodes/fetchPromoCodeDetails',
  async (promoCodeId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const response = await axios.get(`${BASE_URL}/admin/promo-codes/${promoCodeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب تفاصيل البرومو كود');
    }
  }
);

// Create new promo code
export const createPromoCode = createAsyncThunk(
  'adminPromoCodes/createPromoCode',
  async (promoCodeData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const response = await axios.post(`${BASE_URL}/admin/promo-codes`, promoCodeData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في إنشاء البرومو كود');
    }
  }
);

// Update promo code
export const updatePromoCode = createAsyncThunk(
  'adminPromoCodes/updatePromoCode',
  async ({ promoCodeId, promoCodeData }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const response = await axios.post(`${BASE_URL}/admin/promo-codes/${promoCodeId}`, promoCodeData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في تحديث البرومو كود');
    }
  }
);

// Delete promo code
export const deletePromoCode = createAsyncThunk(
  'adminPromoCodes/deletePromoCode',
  async (promoCodeId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      await axios.delete(`${BASE_URL}/admin/promo-codes/${promoCodeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return promoCodeId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في حذف البرومو كود');
    }
  }
);

const adminPromoCodesSlice = createSlice({
  name: 'adminPromoCodes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearPromoCode: (state) => {
      state.promoCode = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch promo codes
      .addCase(fetchPromoCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action) => {
        state.loading = false;
        // التعامل مع الـ response structure الصحيح
        const data = action.payload.data || action.payload;
        state.promoCodes = Array.isArray(data.promo_codes) ? data.promo_codes : [];
        state.pagination = data.pagination || state.pagination;
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch promo code details
      .addCase(fetchPromoCodeDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodeDetails.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload.data || action.payload;
        state.promoCode = data;
      })
      .addCase(fetchPromoCodeDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create promo code
      .addCase(createPromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم إنشاء البرومو كود بنجاح';
        const data = action.payload.data || action.payload;
        if (data.promo_code || data) {
          state.promoCodes.unshift(data.promo_code || data);
        }
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update promo code
      .addCase(updatePromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم تحديث البرومو كود بنجاح';
        const data = action.payload.data || action.payload;
        const promoCodeData = data.promo_code || data;
        if (promoCodeData) {
          const index = state.promoCodes.findIndex(promo => promo.id === promoCodeData.id);
          if (index !== -1) {
            state.promoCodes[index] = promoCodeData;
          }
          if (state.promoCode && state.promoCode.id === promoCodeData.id) {
            state.promoCode = promoCodeData;
          }
        }
      })
      .addCase(updatePromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete promo code
      .addCase(deletePromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'تم حذف البرومو كود بنجاح';
        state.promoCodes = state.promoCodes.filter(promo => promo.id !== action.payload);
        if (state.promoCode && state.promoCode.id === action.payload) {
          state.promoCode = null;
        }
      })
      .addCase(deletePromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, clearPromoCode } = adminPromoCodesSlice.actions;
export default adminPromoCodesSlice.reducer;
